/**
 * Sjablonen: every template in one place — general ones, and those of each
 * database — to use, edit, set up, copy or remove (ADR 0024).
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Copy, Pencil, Play, Plus, Settings2, Trash2 } from 'lucide-react';
import {
  createGeneralTemplate,
  duplicatePage,
  listAllTemplates,
  type TemplateInfo,
} from '@nexus/workspace';
import { useStore, useStoreQuery } from '../db/context';
import { ConfirmDialog } from '../components/Dialog';
import { useCatalog } from '../state/catalog';
import { useUi } from '../state/ui';
import { openUseTemplate, useTemplateUi } from './state';
import { scheduleLabel } from './scheduleLabel';
import { ModuleIcon } from '../components/ModuleIcon';
import { PageIcon } from '../components/PageIcon';

const iconButton =
  'grid size-7 place-items-center rounded-md text-fg-muted hover:bg-bg-hover hover:text-fg';

export function TemplatesView() {
  const { t } = useTranslation();
  const store = useStore();
  const templates = useStoreQuery((s) => listAllTemplates(s), [], ['pages', 'props', 'settings']);
  const [removing, setRemoving] = useState<TemplateInfo | null>(null);

  const general = templates?.filter((x) => !x.database) ?? [];
  const groups = new Map<string, TemplateInfo[]>();
  for (const x of templates ?? [])
    if (x.database) groups.set(x.database.id, [...(groups.get(x.database.id) ?? []), x]);

  const create = async () => {
    const page = await createGeneralTemplate(store, t('templates.newName'));
    useUi.getState().open({ kind: 'page', pageId: page.id });
    useTemplateUi.getState().set({ settingsFor: page.id });
  };

  return (
    <div className="mx-auto w-full max-w-6xl px-6 py-6 sm:px-10" data-testid="templates">
      <header className="mb-6 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-3 text-3xl font-bold tracking-tight">
            <ModuleIcon kind="templates" size="lg" />
            {t('templates.title')}
          </h1>
          <p className="mt-1 text-sm text-fg-muted">{t('templates.intro')}</p>
        </div>
        <button
          type="button"
          onClick={() => void create()}
          className="flex items-center gap-1 rounded-full bg-accent px-3 py-1.5 text-sm font-medium text-accent-fg"
        >
          <Plus className="size-4" />
          {t('templates.new')}
        </button>
      </header>

      <Section title={t('templates.general')}>
        {general.length === 0 && templates && (
          <p className="text-sm text-fg-muted">{t('templates.noneGeneral')}</p>
        )}
        {general.map((x) => (
          <Card key={x.page.id} info={x} onRemove={() => setRemoving(x)} />
        ))}
      </Section>

      {[...groups.values()].map((list) => (
        <Section
          key={list[0]!.database!.id}
          title={t('templates.ofDatabase', { title: list[0]!.database!.title })}
        >
          {list.map((x) => (
            <Card key={x.page.id} info={x} onRemove={() => setRemoving(x)} />
          ))}
        </Section>
      ))}

      <ConfirmDialog
        open={!!removing}
        onOpenChange={(o) => !o && setRemoving(null)}
        title={t('templates.removeTitle', { title: removing?.page.title ?? '' })}
        description={t('templates.removeText')}
        confirmLabel={t('templates.remove')}
        cancelLabel={t('templates.cancel')}
        onConfirm={() => {
          if (removing) void store.trashPage(removing.page.id);
          setRemoving(null);
        }}
      />
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section aria-label={title} className="mb-8">
      <h2 className="mb-2 text-xs font-medium tracking-wide text-fg-muted uppercase">{title}</h2>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">{children}</div>
    </section>
  );
}

function Card({ info, onRemove }: { info: TemplateInfo; onRemove: () => void }) {
  const { t, i18n } = useTranslation();
  const store = useStore();
  const byId = useCatalog((s) => s.byId);
  const { page, config, database } = info;
  const title = page.title || t('page.untitled');
  const target = config.targetId ? byId.get(config.targetId) : null;
  const open = () => useUi.getState().open({ kind: 'page', pageId: page.id });

  return (
    <article
      aria-label={title}
      data-testid="template-card"
      className="flex flex-col gap-2 rounded-xl border border-border bg-bg p-3"
    >
      <button type="button" onClick={open} className="flex items-center gap-2 text-left">
        <span className="text-xl">
          <PageIcon icon={page.icon} fallback={'🧩'} />
        </span>
        <span className="truncate font-medium">{title}</span>
      </button>
      {config.description && <p className="text-sm text-fg-muted">{config.description}</p>}
      <p className="flex flex-wrap gap-1 text-xs text-fg-muted">
        {!database && (
          <span className="rounded bg-bg-subtle px-1.5 py-0.5">
            {target ? `→ ${target.title}` : t('templates.targetTopShort')}
          </span>
        )}
        {config.schedule && (
          <span className="rounded bg-bg-subtle px-1.5 py-0.5">
            🔁 {scheduleLabel(t, config.schedule, i18n.language)}
          </span>
        )}
        {config.daily && (
          <span className="rounded bg-bg-subtle px-1.5 py-0.5">{t('templates.dailyBadge')}</span>
        )}
        {config.fields.length > 0 && (
          <span className="rounded bg-bg-subtle px-1.5 py-0.5">
            {t('templates.questionCount', { count: config.fields.length })}
          </span>
        )}
      </p>
      <div className="mt-auto flex items-center gap-1">
        <button
          type="button"
          onClick={() => openUseTemplate({ templateId: page.id, mode: 'page' })}
          className="flex items-center gap-1 rounded-full bg-accent px-2 py-1 text-xs font-medium text-accent-fg"
        >
          <Play className="size-3.5" />
          {t('templates.use')}
        </button>
        <span className="flex-1" />
        <button
          type="button"
          onClick={open}
          aria-label={t('templates.edit', { title })}
          title={t('templates.edit', { title })}
          className={iconButton}
        >
          <Pencil className="size-3.5" />
        </button>
        {!database && (
          <>
            <button
              type="button"
              onClick={() => useTemplateUi.getState().set({ settingsFor: page.id })}
              aria-label={t('templates.settingsOf', { title })}
              title={t('templates.settingsOf', { title })}
              className={iconButton}
            >
              <Settings2 className="size-3.5" />
            </button>
            <button
              type="button"
              onClick={() => void duplicatePage(store, page.id)}
              aria-label={t('templates.duplicate', { title })}
              title={t('templates.duplicate', { title })}
              className={iconButton}
            >
              <Copy className="size-3.5" />
            </button>
          </>
        )}
        <button
          type="button"
          onClick={onRemove}
          aria-label={t('templates.removeOf', { title })}
          title={t('templates.removeOf', { title })}
          className={`${iconButton} hover:text-danger`}
        >
          <Trash2 className="size-3.5" />
        </button>
      </div>
    </article>
  );
}
